/**
 * Shopify Configuration
 * 
 * This module handles environment variable configuration for Shopify credentials
 * and provides validation and default values for the Shopify Storefront API.
 */

import { ShopifyConfig } from '../types/shopify';
import { debugError, debugWarn } from '../utils/debugLog';

// Environment variable keys
const ENV_KEYS = {
  STORE_DOMAIN: 'REACT_APP_SHOPIFY_STORE_DOMAIN',
  STOREFRONT_ACCESS_TOKEN: 'REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN',
  API_VERSION: 'REACT_APP_SHOPIFY_API_VERSION',
  ADMIN_ACCESS_TOKEN: 'REACT_APP_SHOPIFY_ADMIN_ACCESS_TOKEN', // Optional for future admin features
  WEBHOOK_SECRET: 'REACT_APP_SHOPIFY_WEBHOOK_SECRET', // Optional for webhooks
  ENVIRONMENT: 'REACT_APP_SHOPIFY_ENVIRONMENT'
} as const;

// Default configuration values
const DEFAULT_CONFIG = {
  storeDomain: 'eruption-radio.myshopify.com',
  storefrontAccessToken: '8f7487ff6dd2e28b240af64d8e2fdf70', // Development token
  apiVersion: '2024-01',
  environment: 'development'
} as const;

// Configuration validation errors
export class ShopifyConfigError extends Error {
  constructor(message: string, public field?: string) {
    super(message);
    this.name = 'ShopifyConfigError';
  }
}

/**
 * Validates a store domain format
 */
function validateStoreDomain(domain: string): boolean {
  if (!domain || domain.length === 0) {
    return false;
  }
  
  // Check for invalid patterns
  if (domain.includes('http') || domain.startsWith('.') || domain.endsWith('.') ||
      domain.startsWith('-') || domain.endsWith('-') || domain.includes('--')) {
    return false;
  }
  
  // Check if it's a valid Shopify domain format (must not end with hyphen before .myshopify.com)
  if (domain.includes('.myshopify.com')) {
    const shopifyDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.myshopify\.com$/;
    const subdomain = domain.replace('.myshopify.com', '');
    if (subdomain.endsWith('-') || subdomain.startsWith('-')) {
      return false;
    }
    return shopifyDomainRegex.test(domain);
  }
  
  // Check if it's trying to be a Shopify domain but malformed
  if (domain.includes('myshopify') && !domain.includes('.myshopify.com')) {
    return false;
  }
  
  // Check custom domain format
  const customDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
  return customDomainRegex.test(domain);
}

/**
 * Validates a Shopify Storefront API access token
 */
function validateStorefrontAccessToken(token: string): boolean {
  // Shopify Storefront API tokens are typically 32 characters long and alphanumeric
  const tokenRegex = /^[a-zA-Z0-9]{32}$/;
  return tokenRegex.test(token);
}

/**
 * Validates API version format
 */
function validateApiVersion(version: string): boolean {
  // API version should be in format YYYY-MM
  const versionRegex = /^\d{4}-\d{2}$/;
  if (!versionRegex.test(version)) {
    return false;
  }
  
  // Additional validation for month range
  const [, month] = version.split('-');
  const monthNum = parseInt(month, 10);
  return monthNum >= 1 && monthNum <= 12;
}

/**
 * Gets environment variable value with optional fallback
 */
function getEnvVar(key: string, fallback?: string): string | undefined {
  const value = process.env[key];
  return value || fallback;
}

/**
 * Validates the complete Shopify configuration
 */
function validateConfig(config: ShopifyConfig): void {
  // Validate store domain
  if (!validateStoreDomain(config.storeDomain)) {
    throw new ShopifyConfigError(
      `Invalid store domain: ${config.storeDomain}. Must be a valid Shopify domain (e.g., store.myshopify.com) or custom domain.`,
      'storeDomain'
    );
  }

  // Validate storefront access token
  if (!validateStorefrontAccessToken(config.storefrontAccessToken)) {
    throw new ShopifyConfigError(
      `Invalid Storefront API access token. Token must be 32 alphanumeric characters.`,
      'storefrontAccessToken'
    );
  }

  // Validate API version
  if (!validateApiVersion(config.apiVersion)) {
    throw new ShopifyConfigError(
      `Invalid API version: ${config.apiVersion}. Must be in format YYYY-MM (e.g., 2024-01).`,
      'apiVersion'
    );
  }
}

/**
 * Creates Shopify configuration from environment variables
 */
function createShopifyConfig(): ShopifyConfig {
  const config: ShopifyConfig = {
    storeDomain: getEnvVar(ENV_KEYS.STORE_DOMAIN, DEFAULT_CONFIG.storeDomain)!,
    storefrontAccessToken: getEnvVar(ENV_KEYS.STOREFRONT_ACCESS_TOKEN, DEFAULT_CONFIG.storefrontAccessToken)!,
    apiVersion: getEnvVar(ENV_KEYS.API_VERSION, DEFAULT_CONFIG.apiVersion)!
  };

  // Validate the configuration
  validateConfig(config);

  return config;
}

/**
 * Extended configuration interface with additional optional settings
 */
export interface ExtendedShopifyConfig extends ShopifyConfig {
  adminAccessToken?: string;
  webhookSecret?: string;
  environment: 'development' | 'staging' | 'production';
  enableLogging: boolean;
  enableAnalytics: boolean;
  cacheTimeout: number; // in milliseconds
  retryAttempts: number;
  retryDelay: number; // in milliseconds
}

/**
 * Creates extended Shopify configuration with additional settings
 */
function createExtendedShopifyConfig(): ExtendedShopifyConfig {
  const baseConfig = createShopifyConfig();
  
  const environment = (getEnvVar(ENV_KEYS.ENVIRONMENT, DEFAULT_CONFIG.environment) as ExtendedShopifyConfig['environment']);
  
  const extendedConfig: ExtendedShopifyConfig = {
    ...baseConfig,
    adminAccessToken: getEnvVar(ENV_KEYS.ADMIN_ACCESS_TOKEN),
    webhookSecret: getEnvVar(ENV_KEYS.WEBHOOK_SECRET),
    environment,
    enableLogging: environment === 'development',
    enableAnalytics: environment === 'production',
    cacheTimeout: 60 * 60 * 1000, // 1 hour
    retryAttempts: 3,
    retryDelay: 1000 // 1 second
  };

  return extendedConfig;
}

/**
 * Configuration utilities
 */
export const configUtils = {
  /**
   * Checks if we're in development mode
   */
  isDevelopment: (): boolean => {
    return getEnvVar(ENV_KEYS.ENVIRONMENT, DEFAULT_CONFIG.environment) === 'development';
  },

  /**
   * Checks if we're in production mode
   */
  isProduction: (): boolean => {
    return getEnvVar(ENV_KEYS.ENVIRONMENT, DEFAULT_CONFIG.environment) === 'production';
  },

  /**
   * Gets the Shopify store URL
   */
  getStoreUrl: (config?: ShopifyConfig): string => {
    const domain = config?.storeDomain || getEnvVar(ENV_KEYS.STORE_DOMAIN, DEFAULT_CONFIG.storeDomain);
    return `https://${domain}`;
  },

  /**
   * Gets the Shopify Storefront API URL
   */
  getStorefrontApiUrl: (config?: ShopifyConfig): string => {
    const domain = config?.storeDomain || getEnvVar(ENV_KEYS.STORE_DOMAIN, DEFAULT_CONFIG.storeDomain);
    const version = config?.apiVersion || getEnvVar(ENV_KEYS.API_VERSION, DEFAULT_CONFIG.apiVersion);
    return `https://${domain}/api/${version}/graphql.json`;
  },

  /**
   * Gets the Shopify Admin API URL (if admin token is available)
   */
  getAdminApiUrl: (config?: ExtendedShopifyConfig): string => {
    const domain = config?.storeDomain || getEnvVar(ENV_KEYS.STORE_DOMAIN, DEFAULT_CONFIG.storeDomain);
    const version = config?.apiVersion || getEnvVar(ENV_KEYS.API_VERSION, DEFAULT_CONFIG.apiVersion);
    return `https://${domain}/admin/api/${version}/graphql.json`;
  },

  /**
   * Validates environment variables without throwing
   */
  validateEnvironment: (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    try {
      createShopifyConfig();
    } catch (error) {
      if (error instanceof ShopifyConfigError) {
        errors.push(error.message);
      } else {
        errors.push('Unknown configuration error');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Gets configuration summary for debugging
   */
  getConfigSummary: (includeSecrets: boolean = false): Record<string, any> => {
    const config = createExtendedShopifyConfig();
    
    return {
      storeDomain: config.storeDomain,
      apiVersion: config.apiVersion,
      environment: config.environment,
      enableLogging: config.enableLogging,
      enableAnalytics: config.enableAnalytics,
      cacheTimeout: config.cacheTimeout,
      retryAttempts: config.retryAttempts,
      retryDelay: config.retryDelay,
      hasAdminToken: !!config.adminAccessToken,
      hasWebhookSecret: !!config.webhookSecret,
      ...(includeSecrets && {
        storefrontAccessToken: config.storefrontAccessToken,
        adminAccessToken: config.adminAccessToken,
        webhookSecret: config.webhookSecret
      })
    };
  }
};

// Export the main configuration instances
let shopifyConfig: ShopifyConfig;
let extendedShopifyConfig: ExtendedShopifyConfig;

try {
  shopifyConfig = createShopifyConfig();
  extendedShopifyConfig = createExtendedShopifyConfig();
} catch (error) {
  if (error instanceof ShopifyConfigError) {
    debugError('Shopify Configuration Error:', error.message);
    
    // In development, we can continue with default config
    if (configUtils.isDevelopment()) {
      debugWarn('Using default configuration for development...');
      shopifyConfig = DEFAULT_CONFIG as ShopifyConfig;
      extendedShopifyConfig = {
        ...DEFAULT_CONFIG,
        environment: 'development',
        enableLogging: true,
        enableAnalytics: false,
        cacheTimeout: 60 * 60 * 1000,
        retryAttempts: 3,
        retryDelay: 1000
      } as ExtendedShopifyConfig;
    } else {
      throw error;
    }
  } else {
    throw error;
  }
}

// Export configurations
export { shopifyConfig, extendedShopifyConfig };
export default shopifyConfig;

// Export validation functions for testing
export { validateStoreDomain, validateStorefrontAccessToken, validateApiVersion };

// Export environment keys for reference
export { ENV_KEYS, DEFAULT_CONFIG };
